import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Clock,
  BookmarkCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

/**
 * Primary segment status badge with optional click-to-change popover.
 *
 * Displays the "effective" status of a segment — cancelled and mistake
 * override the base status for display purposes (showing those is more
 * informative than showing the booked/planned state that was true before
 * the segment was archived).
 *
 * Icons are ALWAYS rendered; the text label hides below the sm breakpoint
 * to save space on mobile (icon-only chip).
 *
 * Click → popover with all 5 status transitions. Selecting cancelled/mistake
 * sets the corresponding boolean flag; selecting planned/booked/completed
 * updates `status` AND clears both flags (un-archive).
 *
 * Props:
 *   segment         — full segment object
 *   onStatusChange  — (segment, patch) => Promise<void>. Owner performs the
 *                     PUT and triggers a refresh. When omitted, the badge
 *                     renders as non-interactive (no popover).
 */
const STATUS_OPTIONS = [
  {
    key: "planned",
    label: "Planned",
    Icon: Clock,
    classes: "bg-gray-100 text-gray-800 border-gray-300",
    description: "Still being researched or not yet booked.",
  },
  {
    key: "booked",
    label: "Booked",
    Icon: BookmarkCheck,
    classes: "bg-blue-100 text-blue-800 border-blue-300",
    description: "Reserved and paid (or pending payment).",
  },
  {
    key: "completed",
    label: "Completed",
    Icon: CheckCircle2,
    classes: "bg-green-100 text-green-800 border-green-300",
    description: "Traveled / experienced — done.",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    Icon: XCircle,
    classes: "bg-red-100 text-red-700 border-red-300",
    description: "Zero financial impact; still visible in Payments.",
  },
  {
    key: "mistake",
    label: "Mistake",
    Icon: AlertTriangle,
    classes: "bg-amber-100 text-amber-800 border-amber-300",
    description: "Booking was wrong; money was still spent.",
  },
];

const normalizeKey = (segment) => {
  if (!segment) return "planned";
  if (segment.is_cancelled || segment.status === "cancelled") return "cancelled";
  if (segment.is_mistake) return "mistake";
  const s = (segment.status || "planned").toLowerCase();
  if (s === "confirmed") return "completed"; // legacy → new taxonomy
  if (STATUS_OPTIONS.some(o => o.key === s)) return s;
  return "planned";
};

// Maps a chosen key back to the (status, is_cancelled, is_mistake) tuple the
// backend expects. Selecting a real status clears both archive flags.
const keyToPatch = (key) => {
  if (key === "cancelled") return { is_cancelled: true, is_mistake: false };
  if (key === "mistake") return { is_cancelled: false, is_mistake: true };
  return { status: key, is_cancelled: false, is_mistake: false };
};

export const StatusBadge = ({ segment, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentKey = normalizeKey(segment);
  const current = STATUS_OPTIONS.find(o => o.key === currentKey) || STATUS_OPTIONS[0];
  const Icon = current.Icon;

  const interactive = typeof onStatusChange === "function";

  const badgeInner = (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${current.classes} ${
        interactive ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-sky-300 transition-shadow" : ""
      }`}
      data-testid={`segment-status-badge-${segment?.id || "unknown"}`}
      aria-label={`Status: ${current.label}${interactive ? " (click to change)" : ""}`}
      title={interactive ? `Status: ${current.label} — click to change` : `Status: ${current.label}`}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="hidden sm:inline">{current.label}</span>
    </span>
  );

  if (!interactive) return badgeInner;

  const handleSelect = async (key) => {
    if (key === currentKey || saving) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onStatusChange(segment, keyToPatch(key));
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{badgeInner}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        data-testid="segment-status-popover"
      >
        <p className="text-xs font-semibold text-gray-500 px-2 py-1 uppercase tracking-wide">
          Change status
        </p>
        <div className="flex flex-col gap-1">
          {STATUS_OPTIONS.map(opt => {
            const OptIcon = opt.Icon;
            const isCurrent = opt.key === currentKey;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={saving}
                onClick={() => handleSelect(opt.key)}
                className={`flex items-start gap-2 w-full text-left rounded-md px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 ${
                  isCurrent ? "ring-1 ring-sky-300 bg-sky-50 dark:bg-sky-950/30" : ""
                }`}
                data-testid={`segment-status-option-${opt.key}`}
                aria-current={isCurrent}
              >
                <OptIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${opt.classes.split(" ").find(c => c.startsWith("text-")) || "text-gray-700"}`} />
                <span className="flex-1 min-w-0">
                  <span className="font-medium flex items-center gap-1">
                    {opt.label}
                    {isCurrent && <span className="text-[10px] text-sky-600">(current)</span>}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">{opt.description}</span>
                </span>
              </button>
            );
          })}
        </div>
        {(currentKey === "cancelled" || currentKey === "mistake") && (
          <p className="text-[11px] text-gray-500 px-2 pt-2 border-t mt-2">
            Tip: open Edit to add a reason / notes for this {currentKey} state.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default StatusBadge;
